import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DynamicFormModel } from '@ng-dynamic-forms/core';
import { Cart, OrderEntry } from '@spartacus/cart/base/root';
import { Product } from '@spartacus/core';
import { Order } from '@spartacus/order/root';
import { BehaviorSubject, Subject } from 'rxjs';
import { CheckoutFormsErrorModel } from '../model/checkout.model';
import { DealerPrice } from '../model/dealer-price.model';
import { OrderFormDetails } from '../spartacus/features/cart/order-form/order-form.component';

@Injectable({
  providedIn: 'root',
})
export class ComponentService {
  private customerServiceContentSubject = new Subject<any>();
  customerServiceContent$ = this.customerServiceContentSubject.asObservable();

  private savedCartsListRefreshSubject = new Subject<any>();
  savedCartsListRefresh$ = this.savedCartsListRefreshSubject.asObservable();

  private srpToggleSubject = new Subject<any>();
  srpToggle$ = this.srpToggleSubject.asObservable();

  private dynamicFormModelSubject = new Subject<any>();
  dynamicFormModel$ = this.dynamicFormModelSubject.asObservable();

  private dynamicFormGroupSubject = new Subject<any>();
  dynamicFormGroup$ = this.dynamicFormGroupSubject.asObservable();

  private orderFormTotalSelectedSubject = new Subject<any>();
  orderFormTotalSelected$ = this.orderFormTotalSelectedSubject.asObservable();

  private orderFormTotalPriceSubject = new Subject<any>();
  orderFormTotalPrice$ = this.orderFormTotalPriceSubject.asObservable();

  private orderFormDetailsListSubject = new Subject<any>();
  orderFormDetailsList$ = this.orderFormDetailsListSubject.asObservable();

  private discountTypeSubject = new Subject<any>();
  discountType$ = this.discountTypeSubject.asObservable();

  private orderDetailsSubject = new Subject<any>();
  orderDetails$ = this.orderDetailsSubject.asObservable();

  private isSavingCartSubject = new Subject<any>();
  isSavingCart$ = this.isSavingCartSubject.asObservable();

  private noStockErrorSubject = new Subject<any>();
  noStockError$ = this.noStockErrorSubject.asObservable();

  private searchBoxSuggestionsSubject = new BehaviorSubject<any>(null);
  searchBoxSuggestions$ = this.searchBoxSuggestionsSubject.asObservable();

  private orderFormProdCategoriesSubject = new Subject<any>();
  orderFormProdCategories$ = this.orderFormProdCategoriesSubject.asObservable();

  private dealerPriceSpinnerSubject = new Subject<any>();
  dealerPriceSpinner$ = this.dealerPriceSpinnerSubject.asObservable();

  private productManualSubject = new Subject<any>();
  productManual$ = this.productManualSubject.asObservable();

  private productPDPSubject = new Subject<any>();
  productPDP$ = this.productPDPSubject.asObservable();

  private plpBreadcrumbsSubject = new Subject<any>();
  plpBreadcrumbs$ = this.plpBreadcrumbsSubject.asObservable();

  private siteLogoLinkDisableSubject = new Subject<any>();
  siteLogoLinkDisable$ = this.siteLogoLinkDisableSubject.asObservable();

  private isFloatSubject = new Subject<any>();
  isFloat$ = this.isFloatSubject.asObservable();

  private isMyAccountMenuOpenSubject = new BehaviorSubject<boolean>(false);
  isMyAccountMenuOpen$ = this.isMyAccountMenuOpenSubject.asObservable();

  private isGlobalNavMobileMenuOpenSubject = new BehaviorSubject<boolean>(false);
  isGlobalNavMobileMenuOpen$ = this.isGlobalNavMobileMenuOpenSubject.asObservable();

  private isAddToCartDisabled = new Subject<any>();
  isAddToCartDisabled$ = this.isAddToCartDisabled.asObservable();

  private isLoadAddToCartDisabled = new Subject<any>();
  isLoadAddToCartDisabled$ = this.isLoadAddToCartDisabled.asObservable();

  private holdDeliveryAddress = new Subject<any>();
  holdDeliveryAddress$ = this.holdDeliveryAddress.asObservable();

  private pdpDealerPriceSubject = new Subject<number>();
  pdpDealerPrice$ = this.pdpDealerPriceSubject.asObservable();

  private orderFormGtmDataLayerSubject = new Subject<any>();
  orderFormGtmDataLayer$ = this.orderFormGtmDataLayerSubject.asObservable();

  private updateSavedCartDetails = new Subject<any>();
  updateSavedCartDetails$ = this.updateSavedCartDetails.asObservable();

  private activeCurrency = new BehaviorSubject<string>(null);
  activeCurrency$ = this.activeCurrency.asObservable();

  private activeLanguage = new BehaviorSubject<string>(null);
  activeLanguage$ = this.activeLanguage.asObservable();

  private updateMiniCart = new Subject<any>();
  updateMiniCart$ = this.updateMiniCart.asObservable();

  private resetPdpQty = new Subject<any>();
  resetPdpQty$ = this.resetPdpQty.asObservable();

  private resetMOQAddCart = new Subject<any>();
  resetMOQAddCart$ = this.resetMOQAddCart.asObservable();

  private hasDealerPrice = new Subject<any>();
  hasDealerPrice$ = this.hasDealerPrice.asObservable();

  private hasEvLinks = new BehaviorSubject<boolean>(false);
  hasEvLinks$ = this.hasEvLinks.asObservable();

  private isDiscontinued = new BehaviorSubject<boolean>(false);
  isDiscontinued$ = this.isDiscontinued.asObservable();

  private hasFreeFreight = new BehaviorSubject<boolean>(false);
  hasFreeFreight$ = this.hasFreeFreight.asObservable();

  private freightLevel = new BehaviorSubject<string>(null);
  freightLevel$ = this.freightLevel.asObservable();

  private isUserAdmin = new BehaviorSubject<boolean>(false);
  isUserAdmin$ = this.isUserAdmin.asObservable();

  private accountPermissions = new BehaviorSubject<boolean>(false);
  accountPermissions$ = this.accountPermissions.asObservable();

  private deliveryModeCode = new BehaviorSubject<string>(null);
  deliveryModeCode$ = this.deliveryModeCode.asObservable();

  private discontinuedEnabled = new BehaviorSubject<boolean>(false);
  discontinuedEnabled$ = this.discontinuedEnabled.asObservable();

  selectedHotspotNumber$ = new BehaviorSubject<number>(0);
  selectedHotspotNumberFromImage$ = new BehaviorSubject<number>(0);
  atLeastOneIsSelected$ = new BehaviorSubject<boolean>(false);
  selectedHotspotNumbers$ = new BehaviorSubject<any[]>([]);
  defaultExplodedViewProducts$ = new BehaviorSubject<any>(null);
  bomExplodedView$ = new BehaviorSubject<any>(null);
  defaultExplodedViewProductCodesWithBomItemNoMoq$ = new BehaviorSubject<any>(null);
  defaultExplodedViewDealerPrices$ = new BehaviorSubject<DealerPrice[]>(null);
  expandedImageClicked$ = new BehaviorSubject<boolean>(false);
  shrinkImageClicked$ = new BehaviorSubject<boolean>(false);
  srpToggleClicked$ = new BehaviorSubject<boolean>(false);
  srpToggleValue$ = new BehaviorSubject<boolean>(false);
  initImageContainerWidth$ = new BehaviorSubject<number>(0);
  initImageContainerHeight$ = new BehaviorSubject<number>(0);
  productsCountInPopover$ = new BehaviorSubject<number>(0);
  explodedViewReferrerProduct$ = new BehaviorSubject<Product>(null);
  currentProduct$ = new BehaviorSubject<Product>(null);
  emulatedCustomer$ = new BehaviorSubject<any>(null);
  countryCodeForRequestAccount$ = new BehaviorSubject<string>(null);
  isItemCounterChanged$ = new BehaviorSubject<boolean>(false);
  onDeleteCart$ = new BehaviorSubject<boolean>(false);
  cartEntries$ = new BehaviorSubject<OrderEntry[]>(null);
  isPageLoadedFromLogin$ = new BehaviorSubject<boolean>(false);
  mobilePrefixInput$ = new BehaviorSubject<string>(null);
  gstLabelsOn$ = new BehaviorSubject<boolean>(false);
  checkoutFormsError$ = new BehaviorSubject<CheckoutFormsErrorModel>(null);
  plpDealerPrices$ = new BehaviorSubject<DealerPrice[]>(null);
  plpAppendProducts$ = new BehaviorSubject<boolean>(false);
  alternativeReload$ = new BehaviorSubject<string>(null);
  loadPaymentConfirmation$ = new BehaviorSubject<boolean>(true);
  orderHistoryFilter$ = new BehaviorSubject<any>(null);
  fromProductRefinement$ = new BehaviorSubject<boolean>(false);
  fromSortBy$ = new BehaviorSubject<boolean>(false);
  reloadOrderDetails$ = new Subject<boolean>();
  resetAddEmployeeForm$ = new Subject<boolean>();
  recentlyViewedCookieName$ = new BehaviorSubject<string>(null);
  recentlyViewedUserID$ = new BehaviorSubject<string>(null);
  productCodeJustAddedToCart$ = new BehaviorSubject<any>(null);
  plpPopupEnabled$ = new BehaviorSubject<boolean>(false);
  checkoutCartEntries$ = new BehaviorSubject<Cart>(null);
  checkoutCartEntriesPaymentAddress$ = new BehaviorSubject<Cart>(null);
  checkoutCartEntriesOrderType$ = new BehaviorSubject<Cart>(null);
  orderDetailsEntries$ = new BehaviorSubject<any>(null);
  isB2BUnitFromPrevisSame$ = new BehaviorSubject<boolean>(true);
  standardOrderMinDate$ = new BehaviorSubject<any>(null);
  plannedOrderMinDeliveryDate$ = new BehaviorSubject<any>(null);

  constructor() {}

  setCustomerServiceContent(data: any) {
    this.customerServiceContentSubject.next(data);
  }

  setSavedCartsListRefresh(success: boolean) {
    this.savedCartsListRefreshSubject.next(success);
  }

  setSrpToggle(checked: boolean) {
    this.srpToggleSubject.next(checked);
  }

  setDynamicFormModel(formModel: DynamicFormModel) {
    this.dynamicFormModelSubject.next(formModel);
  }

  setDynamicFormGroup(formGroup: UntypedFormGroup) {
    this.dynamicFormGroupSubject.next(formGroup);
  }

  setOrderFormTotalSelected(total: number) {
    this.orderFormTotalSelectedSubject.next(total);
  }

  setOrderFormTotalPrice(total: number) {
    this.orderFormTotalPriceSubject.next(total);
  }

  setOrderFormDetailsList(orderFormDetails: OrderFormDetails[]) {
    this.orderFormDetailsListSubject.next(orderFormDetails);
  }

  setDiscountType(bucketCode: string) {
    this.discountTypeSubject.next(bucketCode);
  }

  setOrderDetails(orderDetails: Order) {
    this.orderDetailsSubject.next(orderDetails);
  }

  setIsSavingCart(inProgress: boolean) {
    this.isSavingCartSubject.next(inProgress);
  }

  setNoStockError(data: any) {
    this.noStockErrorSubject.next(data);
  }

  setSearchBoxSuggestions(suggestions: string[]) {
    this.searchBoxSuggestionsSubject.next(suggestions);
  }

  setOrderFormProdCategories(data: any) {
    this.orderFormProdCategoriesSubject.next(data);
  }

  setDealerPriceSpinner(code: string) {
    this.dealerPriceSpinnerSubject.next(code);
  }

  setProductManual(productManual: any) {
    this.productManualSubject.next(productManual);
  }

  setProductPDP(triggered: boolean) {
    this.productPDPSubject.next(triggered);
  }

  setPlPBreadcrumbs(breadcrumbs: any) {
    this.plpBreadcrumbsSubject.next(breadcrumbs);
  }

  setSiteLogoLinkDisable(isDisable: any) {
    this.siteLogoLinkDisableSubject.next(isDisable);
  }

  setIsFloat(isFloat: any) {
    this.isFloatSubject.next(isFloat);
  }

  setIsMyAccountMenuOpen(isOpen: any) {
    this.isMyAccountMenuOpenSubject.next(isOpen);
  }

  setIsGlobalNavMobileMenuOpen(isOpen: any) {
    this.isGlobalNavMobileMenuOpenSubject.next(isOpen);
  }

  setIsAddToCartDisabled(disabled: any) {
    this.isAddToCartDisabled.next(disabled);
  }

  setIsLoadAddToCartDisabled(disabled: any) {
    this.isLoadAddToCartDisabled.next(disabled);
  }

  setDeliveryAddress(status: boolean) {
    this.holdDeliveryAddress.next(status);
  }

  setPdpDealerPrice(dealerPrice: number) {
    this.pdpDealerPriceSubject.next(dealerPrice);
  }

  setOrderFormGtmDataLayer(orderFormGtmDataLayer: any) {
    this.orderFormGtmDataLayerSubject.next(orderFormGtmDataLayer);
  }
  setSavedCartDetails(status: boolean) {
    this.updateSavedCartDetails.next(status);
  }

  setActiveCurrency(currency: string) {
    this.activeCurrency.next(currency);
  }

  setActiveLanguage(language: string) {
    this.activeLanguage.next(language);
  }

  setMiniCartLoading(status: boolean) {
    this.updateMiniCart.next(status);
  }

  setResetPdpQty(qty: number) {
    this.resetPdpQty.next(qty);
  }

  setResetMOQAddCart(reset: boolean) {
    this.resetMOQAddCart.next(reset);
  }

  sethasDealerPrice(hasDealerPrice: boolean) {
    this.hasDealerPrice.next(hasDealerPrice);
  }

  setHasEvLinks(hasEvLinks: boolean) {
    this.hasEvLinks.next(hasEvLinks);
  }

  setIsDiscontinued(isDiscontinued: boolean) {
    this.isDiscontinued.next(isDiscontinued);
  }
  sethasFreeFreight(hasFreeFreight: boolean) {
    this.hasFreeFreight.next(hasFreeFreight);
  }
  sethasFreightLevel(freightLevel: string) {
    this.freightLevel.next(freightLevel);
  }
  setUserAdmin(isUserAdmin: any) {
    this.isUserAdmin.next(isUserAdmin);
  }
  setAccountPermissions(accountPermissions: any) {
    this.accountPermissions.next(accountPermissions);
  }
  setSelectedDeliveryModeSNAH(deliveryModeCode: string) {
    this.deliveryModeCode.next(deliveryModeCode);
  }
  setDiscontinuedEnabled(status: boolean) {
    this.discontinuedEnabled.next(status);
  }
}
